import { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';
import { tokenService } from '../services/tokenService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { useNavigate } from 'react-router-dom';
import watermarkImage from '../assets/coverzone-watermark.png';

//const API_URL = process.env.REACT_APP_API_URL;

interface Employee {
  id: number;
  name: string;
  surname: string;
  id_passport_number: string;
  contact_number: string;
  email: string;
  active: boolean;
  re_compliant: boolean;
  online: boolean;
}

const Employees = () => {
  const { can } = usePermissions();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth(); // Use auth context
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState<Omit<Employee, 'id' | 'online'>>({
    name: '',
    surname: '',
    id_passport_number: '',
    contact_number: '',
    email: '',
    active: true,
    re_compliant: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const API_URL = process.env.REACT_APP_API_URL;

  console.log('🌐 axiosInstance baseURL:', axiosInstance.defaults.baseURL);

  useEffect(() => {
  console.log('🔍 Employees component mounted');
  console.log('🔍 Token in localStorage:', localStorage.getItem('token'));
  console.log('🔍 Token from tokenService:', tokenService.getToken());
  console.log('🔍 isAuthenticated:', isAuthenticated);
  console.log('🔍 authLoading:', authLoading);
}, []);

    useEffect(() => {
  console.log('🔍 useEffect running - isAuthenticated:', isAuthenticated, 'authLoading:', authLoading);
  
  const fetchEmployees = async () => {
    try {
      console.log('📤 Fetching employees...');
      
      const token = tokenService.getToken();
      console.log('🔑 Token from service:', token ? `Present (${token.substring(0, 20)}...)` : 'Missing');
      console.log('🔑 Token from localStorage:', localStorage.getItem('token') ? 'Present' : 'Missing');
      
      if (!token) {
        console.warn('⚠️ No token found, redirecting to login');
        navigate('/login');
        return;
      }
      
      setLoading(true);
      console.log('📤 Making GET request to /employees');
      const employeesResponse = await axiosInstance.get('/employees');
      console.log('📋 Employees fetched:', employeesResponse.data.length);
      console.log('📋 Employee data:', JSON.stringify(employeesResponse.data, null, 2));
      
      setEmployees(employeesResponse.data);
      setFilteredEmployees(employeesResponse.data);
      setError(null);
    } catch (err: any) {
      console.error('❌ Error fetching employees:', err);
      console.error('❌ Error response status:', err.response?.status);
      console.error('❌ Error response data:', err.response?.data);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.warn('⚠️ Token expired or invalid');
        tokenService.clearToken();
        logout();
        setError('Session expired. Please log in again.');
        navigate('/login');
      } else {
        setError('Failed to load employees');
        setEmployees([]);
        setFilteredEmployees([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // If not authenticated and not loading, redirect to login
  if (!authLoading && !isAuthenticated) {
    console.warn('⚠️ Not authenticated, redirecting to login');
    navigate('/login');
    return;
  }

  // If authenticated, fetch employees
  if (isAuthenticated) {
    console.log('✅ Authenticated, fetching employees...');
    fetchEmployees();
  } else {
    console.log('⏳ Waiting for authentication...');
  }
}, [isAuthenticated, authLoading, navigate, logout]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (term.trim() === '') {
      setFilteredEmployees(employees);
    } else {
      setFilteredEmployees(
        employees.filter((employee) =>
          employee.name.toLowerCase().includes(term.toLowerCase())
        )
      );
    }
  };

  // New function to handle clicking on an employee name
  const handleEmployeeClick = (employeeId: number, employeeName: string) => {
    navigate(`/employees/${employeeId}`, { state: { employeeName } });
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    console.log(`${type} image loaded successfully`);
  };

  // Add this function to handle the Add Employee button click
  const handleAddEmployee = () => {
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewEmployee({
      name: '',
      surname: '',
      id_passport_number: '',
      contact_number: '',
      email: '',
      active: true,
      re_compliant: true,
    });
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewEmployee({
      ...newEmployee,
      [name]: name === 'active' || name === 're_compliant' ? value === 'true' : value,
    });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!newEmployee.name.trim()) errors.name = 'Name is required';
    if (!newEmployee.surname.trim()) errors.surname = 'Surname is required';
    if (!newEmployee.id_passport_number.trim()) errors.id_passport_number = 'ID/Passport is required';
    if (!newEmployee.contact_number.trim()) errors.contact_number = 'Contact number is required';
    if (!newEmployee.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(newEmployee.email)) {
      errors.email = 'Email is invalid';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  console.log('🚀 handleSubmit FIRED!');
  console.log('📝 Event:', e);
  console.log('📝 Form data:', newEmployee);
  
  e.preventDefault();
  
  console.log('🔍 Validating form...');
  if (!validateForm()) {
    console.log('❌ Validation failed');
    return;
  }
  console.log('✅ Validation passed');

  console.log('📤 Setting loading state...');
  setLoading(true);
  setError(null);
  
  try {
    console.log('📤 Making POST request to /employees...');
    console.log('📤 Data being sent:', {
      name: newEmployee.name,
      surname: newEmployee.surname,
      email: newEmployee.email,
      id_passport_number: newEmployee.id_passport_number,
      contact_number: newEmployee.contact_number,
      active: newEmployee.active,
      re_compliant: newEmployee.re_compliant,
    });
    
    const response = await axiosInstance.post('/employees', {
      name: newEmployee.name,
      surname: newEmployee.surname,
      email: newEmployee.email,
      id_passport_number: newEmployee.id_passport_number,
      contact_number: newEmployee.contact_number,
      active: newEmployee.active,
      re_compliant: newEmployee.re_compliant,
    });

    console.log('✅ Employee added:', response.data);
    console.log('📋 Response status:', response.status);
    
    // Refresh the employee list from the server
    console.log('📤 Refreshing employee list...');
    const employeesResponse = await axiosInstance.get('/employees');
    console.log('📋 Employees after refresh:', employeesResponse.data.length);
    
    setEmployees(employeesResponse.data);
    setFilteredEmployees(employeesResponse.data);
    
    handleCloseModal();
    setError(null);
    console.log('✅ All done!');
    
  } catch (err: unknown) {
    console.error('❌ Error adding employee:', err);
    
    if (axios.isAxiosError(err)) {
      console.log('📋 Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code
      });
      
      // ... rest of error handling
    }
  } finally {
    console.log('📤 Setting loading to false');
    setLoading(false);
  }
};

// Add loading and authentication checks before return
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      {/* Watermark div */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      {/* Sidebar */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      {/* Main content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Employees"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        {/* Page content */}
        <div className="flex-1 p-6">
          {/* Search and Add Employee button */}
          <div className="max-w-4xl mx-auto mb-8 flex items-center gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search employees by name..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-all"
              />
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            {can('employees', 'write') && (
              <button
                onClick={handleAddEmployee}
                className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-all"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Employee
              </button>
            )}
          </div>

          {/* Employees table */}
          <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto w-full">
            {loading && <p className="text-center text-gray-500">Loading...</p>}
            {error && <p className="text-center text-red-500">{error}</p>}
            {!loading && filteredEmployees.length === 0 && !error && (
              <p className="text-center text-gray-500">No employees found.</p>
            )}
            {filteredEmployees.length > 0 && (
              <div className="overflow-x-auto max-h-[600px]">
                <table className="w-full table-auto">
                  <thead className="sticky top-0 bg-gray-100 z-10 shadow-md">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Surname</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Online</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID/Passport Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RE Compliant</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEmployees.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {/* Make the employee name clickable */}
                          <button
                            onClick={() => handleEmployeeClick(employee.id, employee.name)}
                            className="text-blue-600 hover:text-blue-800 focus:outline-none focus:underline"
                          >
                            {employee.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.surname}</td>
                        {/* Display 'Online' status based on the employee.online property */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`h-2.5 w-2.5 rounded-full inline-block mr-1 ${
                            employee.online ? 'bg-green-500' : 'bg-gray-300'
                          }`}></span>
                          {employee.online ? 'Online' : 'Offline'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.id_passport_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.contact_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            employee.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {employee.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            employee.re_compliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {employee.re_compliant ? 'Compliant' : 'Non-Compliant'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Employee</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form id="addEmployeeForm" onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newEmployee.name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    formErrors.name ? 'border-red-500' : 'border'
                  }`}
                />
                {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
              </div>

              <div>
                <label htmlFor="surname" className="block text-sm font-medium text-gray-700">
                  Surname*
                </label>
                <input
                  type="text"
                  id="surname"
                  name="surname"
                  value={newEmployee.surname}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    formErrors.surname ? 'border-red-500' : 'border'
                  }`}
                />
                {formErrors.surname && <p className="mt-1 text-sm text-red-600">{formErrors.surname}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email*
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={newEmployee.email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    formErrors.email ? 'border-red-500' : 'border'
                  }`}
                />
                {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
              </div>

              <div>
                <label htmlFor="id_passport_number" className="block text-sm font-medium text-gray-700">
                  ID/Passport Number*
                </label>
                <input
                  type="text"
                  id="id_passport_number"
                  name="id_passport_number"
                  value={newEmployee.id_passport_number}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    formErrors.id_passport_number ? 'border-red-500' : 'border'
                  }`}
                />
                {formErrors.id_passport_number && <p className="mt-1 text-sm text-red-600">{formErrors.id_passport_number}</p>}
              </div>

              <div>
                <label htmlFor="contact_number" className="block text-sm font-medium text-gray-700">
                  Contact Number*
                </label>
                <input
                  type="text"
                  id="contact_number"
                  name="contact_number"
                  value={newEmployee.contact_number}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    formErrors.contact_number ? 'border-red-500' : 'border'
                  }`}
                />
                {formErrors.contact_number && <p className="mt-1 text-sm text-red-600">{formErrors.contact_number}</p>}
              </div>

              <div>
                <label htmlFor="active" className="block text-sm font-medium text-gray-700">
                  Active Status*
                </label>
                <select
                  id="active"
                  name="active"
                  value={newEmployee.active.toString()}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div>
                <label htmlFor="re_compliant" className="block text-sm font-medium text-gray-700">
                  RE Compliant*
                </label>
                <select
                  id="re_compliant"
                  name="re_compliant"
                  value={newEmployee.re_compliant.toString()}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                >
                  <option value="true">Compliant</option>
                  <option value="false">Not Compliant</option>
                </select>
              </div>

                            {/* Error display */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                type="submit"
                form="addEmployeeForm"
                disabled={loading}
                className={`inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  loading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Adding...' : 'Add Employee'}
              </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;